import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, GraduationCap, BadgeCheck, Sparkles } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1764044371485-b83d1e023d8b?crop=entropy&cs=srgb&fm=jpg&q=85";

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative bg-navy text-white overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 hero-grid-lines" />
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-brand/25 blur-[140px] pointer-events-none"
      />
      <div className="absolute -bottom-56 -left-40 w-[480px] h-[480px] rounded-full bg-gold/15 blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-bold tracking-[0.18em] uppercase text-gold mb-7"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={13} /> Ready. Set. Learn.
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[64px] text-balance" data-testid="hero-headline">
            <MaskedLines
              lines={["Everything for a", "better school year."]}
              accentIndex={1}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 text-white/70 text-base lg:text-lg max-w-md leading-relaxed"
          >
            From essential stationery to the prescribed textbooks on your list — find what you need,
            shop with confidence, and choose delivery or collection.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/shop"
              className="h-13 px-7 py-3.5 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm flex items-center gap-2 transition-colors gold-glow"
              data-testid="hero-shop-cta"
            >
              Shop the collection <ArrowRight size={16} />
            </Link>
            <Link
              to="/schools"
              className="h-13 px-7 py-3.5 rounded-full border border-white/25 hover:bg-white/10 text-white font-bold text-sm flex items-center gap-2 transition-colors"
              data-testid="hero-schools-cta"
            >
              <GraduationCap size={16} /> School orders
            </Link>
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[28px] overflow-hidden spotlight-img border border-white/10"
          >
            <img src={HERO_IMG} alt="School supplies" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/70 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="absolute -left-8 top-10 bg-white text-navy rounded-2xl px-4 py-3 shadow-xl animate-float-slow flex items-center gap-2.5"
            data-testid="hero-chip-ready"
          >
            <span className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
              <BadgeCheck size={16} className="text-gold-hover" />
            </span>
            <span className="text-xs font-bold">Back-to-school ready</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="absolute -right-6 bottom-12 bg-navy-light border border-white/15 rounded-2xl px-4 py-3 shadow-xl animate-float-slow"
            style={{ animationDelay: "1.2s" }}
            data-testid="hero-chip-essentials"
          >
            <p className="text-[10px] uppercase tracking-widest text-gold font-bold">Everyday essentials</p>
            <p className="text-sm font-display font-bold">Learn. Create.</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
